import getMailListViewState from 'mini-mail-list-store/lib/utils/getMailListViewState';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import listViewStore from 'mini-mail-list-store/lib/store/Store';
import type MailListViewState from 'mini-mail-list-store/lib/store/schema/MailListViewState';
import React from 'react';
import type TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import { BINARY_SWITCH } from 'mini-mail-list-store/lib/store/schema/MailHeaderElementToFocus';
import { format } from 'owa-strings';
import { getAllOnboardingCardsShownStatus } from 'mini-mail-nativead-userchecker/lib/getAllOnboardingCardsShownStatus';
import { getNativeAdUserFlight } from './helpers/getNativeAdUserFlight';
import { isMiniFeatureEnabled } from 'owa-mini-feature-flags';
import { LazyMailListEmpty } from './MailListEmpty';
import { measureMailListInitialLoadPerformance } from './helpers/measureMailListInitialLoadPerformance';
import { observer } from 'mobx-react';
import { type PlacementId } from 'mini-campaign-bootstrap/lib/schema/PlacementId';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';
import { VirtualizedMailList } from './VirtualizedListView';
import type { RichContentFlags } from './VirtualizedListView/DisplayCustomisationsProvider';
import { DisplayCustomisationsProvider } from './VirtualizedListView/DisplayCustomisationsProvider';
import {
    ARC_API_CALL_COMPLETED,
    campaignEventEmitter,
} from 'mini-campaign/lib/utils/campaignEventEmitters';

export interface MailListProps {
    isMailListShown: () => boolean;
    tableView: TableView;
}

@observer
export default class MailList extends React.Component<MailListProps> {
    private switchRef = React.createRef<HTMLDivElement>();
    private filterRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        const tableView = this.props.tableView;
        if (tableView) {
            const mailListLoadState: MailListViewState = getMailListViewState();
            measureMailListInitialLoadPerformance(tableView, mailListLoadState, 'mailListMount');
        }

        campaignEventEmitter.on(format(ARC_API_CALL_COMPLETED, '88000759'), ev => {
            const areAllOnboardingCardsShown = getAllOnboardingCardsShownStatus();
            if (ev.isIrisCallSuccess && !ev.isCampaignActive) {
                if (!areAllOnboardingCardsShown) {
                    /**
                     *If there are no new campaigns, and the previous value for 'allOnboardingCardsShown' is false
                     set the value to true
                    */
                    import('./helpers/setAllOnboardingCardsShownStatus').then(module =>
                        module.setAllOnboardingCardsShownStatus()
                    );
                }
            }
        });
    }

    componentDidUpdate(prevProps: MailListProps) {
        const tableView = this.props.tableView;

        if (tableView) {
            const mailListLoadState: MailListViewState = getMailListViewState();
            const conversationsOrigin =
                prevProps.tableView?.id !== tableView.id
                    ? 'conversationsInStore'
                    : 'conversationsFetched';
            measureMailListInitialLoadPerformance(
                tableView,
                mailListLoadState,
                conversationsOrigin
            );

            if (tableView.mailHeaderElementToFocus) {
                const element =
                    tableView.mailHeaderElementToFocus === BINARY_SWITCH
                        ? this.switchRef
                        : this.filterRef;
                trySetFocusOnElement(element);
            }
        }
    }

    private renderListViewContent(): JSX.Element {
        const tableView = getSelectedTableView();
        const richContentFlagsForUser: RichContentFlags = {
            shouldShowRSVPUnit: true,
            shouldShowSuggestedUnsubscribeUnit: isMiniFeatureEnabled('mini-suggested-unsubscribe'),
        };

        const showUpNext = listViewStore?.showUpNext || false;
        const showUSQWarningCard = listViewStore?.showUSQWarningCard || false;
        const onboardingCardsHeight = listViewStore?.onboardingCardsHeight || 0;

        return (
            <DisplayCustomisationsProvider
                richContentFlags={richContentFlagsForUser}
                showUpNext={showUpNext}
                showUSQWarningCard={showUSQWarningCard}
                onboardingCardHeight={onboardingCardsHeight}>
                <VirtualizedMailList
                    showNativeAdFormat={getNativeAdUserFlight(tableView)}
                    forwardedRefs={{ switchRef: this.switchRef, filterRef: this.filterRef }}
                    enableSwipeRefresh
                />
            </DisplayCustomisationsProvider>
        );
    }

    render() {
        const mailListViewState = getMailListViewState();

        switch (mailListViewState) {
            case 0:
            case 5:
                return this.renderListViewContent();
            default:
                return (
                    <LazyMailListEmpty
                        mailListViewState={mailListViewState}
                        switchRef={this.switchRef}
                        filterRef={this.filterRef}
                    />
                );
        }
    }
}
